"use strict";

/**
 * End of day (SPEC-CIS-COCKPIT-DAY-REDESIGN-P2-001 2.1, 2.2, 2.3): the pure part.
 *
 *  - shapeEndOfDay      the Today envelope (`day` objects) -> the screen's model
 *  - buildApproveItems  the ticked records -> POST /api/cockpit/records/review items
 *  - buildCloseItem     one side -> the schedule upsert close item
 *  - submitEndOfDay     the one submit: approve the ticked records, then carry the
 *                       postponed sides still set to carry; stale_etag stops it
 *
 * Nothing here touches the network: the two writes are passed in, so the same
 * code runs live, in fixture mode and under test.
 */

import { dayHeaderLabel, formatDay, initialsName, shapeDayLine } from "./shape.js";

export const NOT_ATTENDED_MAX = 255;
export const MAX_REVIEW_ITEMS = 50;

export const STALE_MESSAGE = "This day changed a moment ago. Check it and press Approve again.";
export const CONFLICT_MESSAGE = "This side was recorded or closed a moment ago. The day was reloaded.";

const DAY_MS = 86400000;

/** The next calendar day, skipping a Sunday (the server skips a Sunday that has no booking rows). */
export function nextWorkingDay(date) {
  const start = Date.parse(`${date}T00:00:00Z`);
  if (!Number.isFinite(start)) return "";
  let next = new Date(start + DAY_MS);
  if (next.getUTCDay() === 0) next = new Date(next.getTime() + DAY_MS);
  return next.toISOString().slice(0, 10);
}

/** "Wed 28 Oct" */
export function nextWorkingDayLabel(date) {
  const next = nextWorkingDay(date);
  return next ? formatDay(`${next}T00:00:00Z`) : "";
}

const RESULT_CHIP = {
  accepted: { text: "ACCEPTED", tone: "ok" },
  "accepted with comments": { text: "WITH COMMENTS", tone: "warn" },
  "not accepted": { text: "NOT ACCEPTED", tone: "bad" },
  postponed: { text: "POSTPONED", tone: "mute" },
};

export function resultChip(result) {
  return RESULT_CHIP[String(result || "").trim().toLowerCase()] || { text: String(result || "").toUpperCase(), tone: "mute" };
}

function who(upn, me) {
  const key = String(upn || "").trim().toLowerCase();
  if (!key) return "not assigned";
  return key === String(me || "").trim().toLowerCase() ? "site manager" : "assistant inspector";
}

function sideEntry(line, side, projectCode, me) {
  const sideName = side.side ? ` · ${side.side}` : "";
  return {
    key: `${projectCode}|${side.scheduleRef}`,
    lineKey: line.key,
    item: line.item,
    projectCode,
    scheduleRef: side.scheduleRef,
    etag: side.etag || "",
    state: side.state,
    timeText: line.timeText,
    title: `${line.item}${sideName}`,
    side: side.side,
    place: line.place,
    who: who(side.assignedTo, me),
    result: side.result || "",
    carriedTo: side.carriedTo || "",
    notAttendedReason: side.notAttendedReason || "",
  };
}

function recordEntry(record, projectCode) {
  const chip = resultChip(record.result);
  return {
    key: `${projectCode}|${record.inspectionNumber}`,
    projectCode,
    inspectionNumber: record.inspectionNumber,
    etag: record.etag || "",
    time: record.time || "—",
    title: [[record.block, record.title].filter(Boolean).join(" · "), record.scheduled === true ? "" : "no yard request"].filter(Boolean).join(" · "),
    block: record.block || "",
    side: record.side || "",
    sideText: record.side || "—",
    result: chip.text,
    resultTone: chip.tone,
    defectNoted: record.defectNoted === true,
    defect: record.defectNoted === true ? "Defect noted" : "None",
    photoCount: Number(record.photoCount) || 0,
    capturedBy: record.capturedBy ? initialsName(record.capturedBy) : "",
    scheduled: record.scheduled === true,
  };
}

/** The postponed sides of one line are one row ("BG6 · welding inspection · P and S"): kept or carried together. */
function groupPostponed(sides) {
  const groups = [];
  const index = new Map();
  for (const s of sides) {
    let g = index.get(s.lineKey);
    if (!g) {
      g = { key: s.lineKey, projectCode: s.projectCode, timeText: s.timeText, item: s.item, who: s.who, sides: [] };
      index.set(s.lineKey, g);
      groups.push(g);
    }
    g.sides.push(s);
  }
  for (const g of groups) {
    const letters = g.sides.map((x) => x.side).filter(Boolean).sort();
    g.title = `${g.item}${letters.length === 2 ? " \u00b7 P and S" : letters.length === 1 ? ` \u00b7 ${letters[0]}` : ""}`;
  }
  return groups;
}

/**
 * @param {object} envelope  the Today envelope, already narrowed to the selected projects
 * @param {{me?: string}} opts
 */
export function shapeEndOfDay(envelope, { me = "" } = {}) {
  const projects = ((envelope && envelope.projects) || []).filter((p) => p && p.day);
  const date = (projects[0] && projects[0].day.date) || "";
  const noResult = [];
  const postponed = [];
  const closed = [];
  const records = [];
  const counts = { requested: 0, recorded: 0, postponed: 0, unscheduled: 0, toGo: 0, carriedOver: 0, notAttended: 0 };
  let approvedCount = 0;
  for (const p of projects) {
    const c = p.day.counts || {};
    for (const k of Object.keys(counts)) counts[k] += Number(c[k]) || 0;
    approvedCount += Number(p.day.approvedCount) || 0;
    for (const raw of p.day.lines || []) {
      const line = shapeDayLine(raw, p.day, me, p.code);
      for (const side of line.sides) {
        const entry = sideEntry(line, side, p.code, me);
        if (side.state === "to_go") noResult.push(entry);
        else if (side.state === "postponed") postponed.push(entry);
        else if (side.state === "carried" || side.state === "not_attended") closed.push(entry);
      }
    }
    for (const r of p.day.toApprove || []) records.push(recordEntry(r, p.code));
  }
  const byTime = (a, b) => a.timeText.localeCompare(b.timeText) || a.title.localeCompare(b.title);
  noResult.sort(byTime);
  postponed.sort(byTime);
  closed.sort(byTime);
  const multiProject = new Set(projects.map((p) => p.code)).size > 1;
  return {
    date,
    dateLabel: date ? formatDay(`${date}T00:00:00Z`) : "",
    headerLabel: dayHeaderLabel(date),
    carryLabel: nextWorkingDayLabel(date),
    counts,
    summary: {
      recordedText: `${counts.recorded} of ${counts.requested} requested sides recorded`,
      postponedText: counts.postponed ? `${counts.postponed} postponed` : "",
      unscheduledText: counts.unscheduled ? `${counts.unscheduled} recorded without a yard request` : "",
      noResultText: counts.toGo ? `${counts.toGo} with no result` : "",
      closedText: [counts.carriedOver ? `${counts.carriedOver} carried` : "", counts.notAttended ? `${counts.notAttended} not attended` : ""].filter(Boolean).join(" · "),
    },
    noResult,
    postponed: groupPostponed(postponed),
    closed,
    records,
    approvedCount,
    withDefects: records.filter((r) => r.defectNoted).length,
    withoutPhotos: records.filter((r) => r.photoCount === 0).length,
    withoutRequest: records.filter((r) => !r.scheduled).length,
    projectCodes: projects.map((p) => p.code),
    multiProject,
    available: projects.length > 0,
  };
}

/** The ticked records as review items (one per record, `approve`). */
export function buildApproveItems(records) {
  return (records || []).map((r) => ({ inspectionNumber: r.inspectionNumber, etag: r.etag, op: "approve" }));
}

/** 1 to 255 characters after trimming. */
export function validateReason(text) {
  const reason = typeof text === "string" ? text.trim() : "";
  if (!reason) return { ok: false, message: "Enter why this was not attended." };
  if (reason.length > NOT_ATTENDED_MAX) return { ok: false, message: `Use at most ${NOT_ATTENDED_MAX} characters.` };
  return { ok: true, reason };
}

/** One close item for the schedule upsert: exactly { scheduleRef, etag, close } plus `reason` for not attended. */
export function buildCloseItem(side, close, reason) {
  const item = { scheduleRef: side.scheduleRef, etag: side.etag, close };
  if (close === "not_attended") item.reason = reason;
  return item;
}

function chunk(list, size) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

function groupByProject(list) {
  const map = new Map();
  for (const x of list) {
    if (!map.has(x.projectCode)) map.set(x.projectCode, []);
    map.get(x.projectCode).push(x);
  }
  return map;
}

const CLOSE_OK = ["ok", "duplicate"];

/** Reads one upsert result list into { kind, message } for a set of close items. */
function readCloseResults(items, result) {
  const body = result && result.ok && Array.isArray(result.body) ? result.body : null;
  if (!body) return { kind: "error", message: `The day could not be updated (${(result && result.body && result.body.errorCode) || `http_${(result && result.status) || 0}`}).` };
  let carried = 0;
  for (let i = 0; i < items.length; i += 1) {
    const r = body[i];
    if (r && CLOSE_OK.includes(r.status)) {
      carried += 1;
      continue;
    }
    if (r && (r.status === "stale_etag" || r.status === "conflict")) return { kind: r.status === "stale_etag" ? "stale" : "conflict", message: r.status === "stale_etag" ? STALE_MESSAGE : CONFLICT_MESSAGE, carried };
    if (r && r.status === "invalid") return { kind: "error", message: `The server refused this item (${(r.errors || []).map((e) => (Array.isArray(e) ? e[1] : e)).join(", ") || "invalid"}).`, carried };
    return { kind: "error", message: `The day could not be updated (${(r && r.errorCode) || (r && r.status) || "no answer"}).`, carried };
  }
  return { kind: "ok", carried };
}

/** Carry or mark one side right away. Resolves { kind: "ok"|"stale"|"conflict"|"error", message }. */
export async function closeSide({ side, close, reason, upsert }) {
  const item = buildCloseItem(side, close, reason);
  let result;
  try {
    result = await upsert(side.projectCode, [item]);
  } catch (err) {
    return { kind: "error", message: (err && err.message) || "request failed" };
  }
  return readCloseResults([item], result);
}

/**
 * The one submit (2.1): approve the ticked records, then carry the postponed
 * sides still set to carry. A stale_etag on any record stops the submit before
 * any carry is sent, and nothing is resent: the caller reloads the day and the
 * site manager presses Approve again.
 *
 * @param {{records: object[], carries: object[], review: Function, upsert: Function}} args
 * @returns {Promise<{kind: "ok"|"stale"|"conflict"|"error"|"partial", approved: number, carried: number, message: string}>}
 */
export async function submitEndOfDay({ records, carries, review, upsert }) {
  let approved = 0;
  let carried = 0;
  const failed = [];
  for (const [projectCode, list] of groupByProject(records || [])) {
    for (const part of chunk(list, MAX_REVIEW_ITEMS)) {
      let result;
      try {
        result = await review(projectCode, buildApproveItems(part));
      } catch (err) {
        return { kind: "error", approved, carried, message: (err && err.message) || "request failed" };
      }
      const body = result && result.ok && Array.isArray(result.body) ? result.body : null;
      if (!body) return { kind: "error", approved, carried, message: `The records could not be approved (${(result && result.body && result.body.errorCode) || `http_${(result && result.status) || 0}`}).` };
      for (let i = 0; i < part.length; i += 1) {
        const r = body[i];
        if (r && r.status === "ok") approved += 1;
        else if (r && r.status === "stale_etag") return { kind: "stale", approved, carried, message: STALE_MESSAGE };
        else failed.push(part[i]);
      }
    }
  }
  for (const [projectCode, list] of groupByProject(carries || [])) {
    for (const part of chunk(list, MAX_REVIEW_ITEMS)) {
      const items = part.map((s) => buildCloseItem({ ...s, projectCode }, "carry"));
      let result;
      try {
        result = await upsert(projectCode, items);
      } catch (err) {
        return { kind: "error", approved, carried, message: (err && err.message) || "request failed" };
      }
      const read = readCloseResults(items, result);
      carried += read.carried || 0;
      if (read.kind !== "ok") return { kind: read.kind, approved, carried, message: read.message };
    }
  }
  if (failed.length) {
    return {
      kind: "partial",
      approved,
      carried,
      message: `${failed.length} ${failed.length === 1 ? "record" : "records"} could not be approved. Check the list and press Approve again.`,
    };
  }
  return { kind: "ok", approved, carried, message: "" };
}
